<template>
  <SogPage
    id="page"
    :title="_person?.personName"
  >
    <SogCard>
      <SogCardActions :is-toolbar="true">
        <SogCardActionButton
          v-show="_page==='details'"
          mdi-icon="mdi-content-save"
          tooltip="Speichern"
          @click="onSave"
        >
          Speichern
        </SogCardActionButton>
        <SogCardActionButton
          :kind="SogButtonKind.Back"
          @click="onBack"
        />
      </SogCardActions>
      <SogCardContent v-show="_basePrice">
        <ProductPriceCardComponent
          :price="_basePrice"
        />
      </SogCardContent>
      <SogCardContent>
        <PriceListComponent
          v-show="_action==='add'&&_page==='base' && _prices.length>0"
          :prices="_prices"
          allow-select="true"
          :selected="onSelected"
        />
        <SogForm v-show="_page==='details'">
          <table>
            <tr v-show="_visiblePrice">
              <td>
                <SogFormField>
                  <SogNumericTextBox
                    v-model="_grossPrice"
                    :placeholder="_grossPriceLabel"
                    width="150px"
                    :min="_basePrice?.minSaleGrossPrice??_basePrice?.grossPrice"
                  />
                </SogFormField>
              </td>
              <td>
                <SogFormField>
                  <SogNumericTextBox
                    v-model="_shortPrice"
                    :placeholder="_smallPriceLabel"
                    width="150px"
                    :min="_basePrice?.minSaleShortPrice??_basePrice?.shortPrice"
                  />
                </SogFormField>
              </td>
            </tr>
            <tr>
              <td colspan="2">
                <SogFormField>
                  <SogTextBox
                    v-model="_productRefId"
                    placeholder="Produktreferenznummer"
                  />
                </SogFormField>
              </td>
            </tr>
          </table>
        </SogForm>
      </SogCardContent>
    </SogCard>
  </SogPage>
</template>

<script setup lang="ts">
import SogPage from "@/shared/ui/SogPage.vue";
import {onMounted, reactive, ref, watch} from "vue";
import {onBeforeRouteLeave, onBeforeRouteUpdate, RouteLocationNormalizedLoaded, useRoute, useRouter} from "vue-router";
import Person from "@/shared/model/person";
import SogCard from "@/shared/ui/SogCard.vue";
import SogCardContent from "@/shared/ui/SogCardContent.vue";
import SogCardActions from "@/shared/ui/SogCardActions.vue";
import SogCardActionButton from "@/shared/ui/SogCardActionButton.vue";
import PriceListComponent from "@/modules/OrganisationStructure/components/PriceListComponent.vue";
import getGuid from "@/shared/utils/getGuid";
import apiOrganisationStructure from "@/modules/OrganisationStructure/api";
import {useAccountStore} from "@/stores/accountStore";
import ProductPrice from "@/shared/model/productPrice";
import SogForm from "@/shared/ui/SogForm.vue";
import SogFormField from "@/shared/ui/SogFormField.vue";
import SogNumericTextBox from "@/shared/ui/SogNumericTextBox.vue";
import copyTo from "@/shared/utils/CopyTo";
import notificationService from "@/shared/services/NotificationService";
import {computed} from "vue";
import SogTextBox from "@/shared/ui/SogTextBox.vue";
import {useSpinner} from "@/shared/composables/SogSpinner";
import {useOrganisationStructureStore} from "@/modules/OrganisationStructure/store";
import {storeToRefs} from "pinia";
import {SogButtonKind} from "@/shared/ui/types/SogButtonKind";
import ProductPriceCardComponent from "@/modules/OrganisationStructure/components/ProductPriceCardComponent.vue";


const _router = useRouter()
const _accountStore = useAccountStore();
const _person = ref<Person | undefined>(undefined)
const _type = ref("")
const _action = ref("")
const _page = ref("base")
const _prices = reactive<ProductPrice[]>([])
const _grossPrice = ref(0)
const _shortPrice = ref(0)
const _productRefId = ref<string | undefined>(undefined)
const _basePrice = ref<ProductPrice | undefined>(undefined)
const _price = ref<ProductPrice | undefined>(undefined)
const _spinner = useSpinner();
const _organisationStructureStore = useOrganisationStructureStore();
const {productPriceEditPage: _productPriceEditPage} = storeToRefs(_organisationStructureStore)


async function p_initBase() {
  const key = getGuid();
  const id = _accountStore.person?.id ?? "";
  const prices: ProductPrice[] = []

  const res = await apiOrganisationStructure.loadProductPrice(key, id, true)
  if (res?.prices && res?.key === key) {
    prices.push(...res.prices.filter(t => {
      if (t.productTypeId === "ExportBlank") {
        return (_accountStore.isRoot || _person.value?.isBranch()|| _person.value?.isBranchTemplate()) && !_productPriceEditPage.value.productPrices?.some(p => p.productKey === t.productKey)
      }
      return !_productPriceEditPage.value.productPrices?.some(p => p.productKey === t.productKey)
    }))
  }

  _prices.length = 0;
  if (_action.value === "add") {
    _prices.push(...prices.sort((a, b) => {
      const diff = a?.getSortIndex() - b?.getSortIndex();
      if (diff) return diff;
      return a.productName?.localeCompare(b.productName ?? "") ?? 0;
    }))

    if (_prices.length === 0) {
      notificationService.warn("Sie haben keine Produkte, die Sie verkaufen können")
      await _router.back()
      return
    }
  }
  if (_action.value === "edit") {
    if (!_price.value) {
      notificationService.warn("Produktprise ist nicht definiert")
      await _router.back()
      return
    }
    const array = prices.filter(t => t.productKey === _price.value?.productKey)
    if (array.length === 0) {
      notificationService.errorThen("Sie können Produkt nicht einkaufen.", async () => {
        await _router.back()
      })
      return
    }
    await onSelected(array[0])
  }
}

async function p_init(route: RouteLocationNormalizedLoaded) {
  if (!_productPriceEditPage.value.person) {
    _router.back()
    return
  }
  _type.value = route.params.type as string ?? "";
  _action.value = route.params.action as string ?? "";
  _page.value = (route.params.page as string) === "" ? "base" : route.params.page as string;

  _person.value = _productPriceEditPage.value.person
  _basePrice.value = _productPriceEditPage.value.basePrice
  _price.value = _productPriceEditPage.value.price


  if (_page.value === "base") {
    await p_initBase();
  }
  if (_page.value === "details") {
    p_initDetails();
  }
}

function p_initDetails() {
  if (_price.value) {
    _productRefId.value = _price.value.productRefId;
    _grossPrice.value = (_person.value?.isBranch() && _price.value.minSaleGrossPrice) ? _price.value.minSaleGrossPrice : _price.value.grossPrice;
  }
}

onMounted(async () => {
  const route = useRoute();
  await p_init(route)
})

onBeforeRouteUpdate(async (to) => {
  await p_init(to)
});

onBeforeRouteLeave(() => {
  /*
    _clipboardStore.person = undefined
    _clipboardStore.setProductPrices([])
  */
})

async function onBack() {
  if (_page.value === "base") {
    _organisationStructureStore.clearProductPriceEditPage()
  }
  if (_page.value === "details" && _action.value == "edit") {
    _organisationStructureStore.clearProductPriceEditPage()
  }
}

async function onSave() {
  if (!_basePrice.value) {
    notificationService.warn("Wählen Sie Basispreis aus.")
    return
  }

  _spinner.show()
  try {
    let addPrice: ProductPrice | undefined = undefined;
    let editPrice: ProductPrice | undefined = undefined;
    const price = new ProductPrice();
    const ownerId = _accountStore.person?.id
    const id = _type.value === "sale" ? _person.value?.id : _accountStore.person?.id
    const supplierId = _type.value === "sale" ? _accountStore.person?.id : _person.value?.id

    if (_action.value === "add") {
      copyTo(_basePrice.value, price)
      price.id = _action.value === "add" ? undefined : price.id
      price.fromDate = undefined
      price.tillDate = undefined
      price.supplierId = supplierId
      price.personId = id;
      if (price.productTypeId === "Zoll" && _person.value?.isBranch()) {
        price.saleTypes = price.saleTypes.filter(t => t !== "JustInTime")
      }
      addPrice = price
    }
    if (_action.value === "edit") {
      copyTo(_price.value, price)
      editPrice = price
    }

    price.productRefId = _productRefId.value
    price.grossPrice = _visiblePrice.value ? _person.value?.isBranch() ? _basePrice.value.grossPrice : _grossPrice.value : 0;
    price.minSaleGrossPrice = _person.value?.isBranch() ? _grossPrice.value : undefined

    const res = await apiOrganisationStructure.updateDetailsPrice(ownerId, id, addPrice, editPrice)
    if (res) {
      notificationService.info("Produktprise wurde gespeichert")
      _organisationStructureStore.clearProductPriceEditPage()
      await _router.push("/organisationstructure/details")
    }
  } finally {
    _spinner.hide()
  }
}

const _grossPriceLabel = computed(() => {
  return _person.value?.isBranch() ? "Min.Prämie inkl. VersSt., €" : "Prämie inkl. VersSt., €"
})
const _smallPriceLabel = computed(() => {
  return _person.value?.isBranch() ? "Min.Prämie exkl. VersSt, €" : "Prämie exkl. VersSt, €"
})

const _visiblePrice = computed(() => {
  return _type.value === "sale" ? _basePrice.value && _basePrice.value.productTypeId !== "ExportBlank" : false
})

async function onSelected(price?: ProductPrice) {
  _productPriceEditPage.value.basePrice = price;
  await _router.push("/organisationstructure/price/" + _action.value + "/" + _type.value + "/details")
}

watch((_shortPrice), () => {
  const res = Number((_shortPrice.value * ((_basePrice.value?.tax ?? 0) + 100) / 100).toFixed(2))
  if (res !== _grossPrice.value)
    _grossPrice.value = res
})

watch((_grossPrice), () => {
  const res = Number((_grossPrice.value * 100 / ((_basePrice.value?.tax ?? 0) + 100)).toFixed(2))
  if (_shortPrice.value !== res)
    _shortPrice.value = res
})


</script>
<style scoped>
</style>
